import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import supabase from '../utils/supabaseClient';
import { normalizeDifficulty, selectRecommendedExercises } from '../utils/exerciseRecommendations';
import './ExerciseBrowser.css';

const ExerciseBrowser = () => {
    const { isDarkTheme } = useTheme();
    const { user, profile } = useAuth();
    const navigate = useNavigate();

    const [units, setUnits] = useState([]);
    const [exercises, setExercises] = useState([]);
    const [selectedUnit, setSelectedUnit] = useState(null);
    const [loading, setLoading] = useState(true);
    const [exercisesLoading, setExercisesLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [completedIds, setCompletedIds] = useState(new Set());
    const [submissions, setSubmissions] = useState([]);
    const [recommendationMode, setRecommendationMode] = useState('balanced');

    // Load units
    useEffect(() => {
        const fetchUnits = async () => {
            setLoading(true);
            try {
                const { data, error } = await supabase
                    .from('units')
                    .select('id, title, description')
                    .order('title', { ascending: true });

                if (error) throw error;
                setUnits(data || []);
            } finally {
                setLoading(false);
            }
        };
        fetchUnits();
    }, []);

    // Load completed exercise IDs for the user
    useEffect(() => {
        if (!user) return;
        const fetchSubmissions = async () => {
            try {
                const { data, error } = await supabase
                    .from('submissions')
                    .select('exercise_id, is_correct, created_at')
                    .eq('user_id', user.id);
                if (!error && data) {
                    setSubmissions(data);
                    setCompletedIds(new Set(data.filter((submission) => submission.is_correct).map((submission) => submission.exercise_id)));
                }
            } catch (err) {
                // submissions table may not exist yet — that's ok
            }
        };
        fetchSubmissions();
    }, [user]);

    // Load exercises when unit selected
    const fetchExercises = useCallback(async (unitId) => {
        setExercisesLoading(true);
        try {
            let query = supabase
                .from('exercises')
                .select('*')
                .eq('status', 'approved')
                .order('created_at', { ascending: true });

            if (unitId) {
                query = query.eq('unit_id', unitId);
            }

            if (searchQuery.trim()) {
                query = query.ilike('title', `%${searchQuery.trim()}%`);
            }

            const { data, error } = await query.limit(100);
            if (error) throw error;
            const userLanguage = profile?.language_preference
                || user?.user_metadata?.language_preference
                || user?.user_metadata?.language
                || null;
            const userSchoolLevel = profile?.school_level
                || profile?.education_level
                || user?.user_metadata?.school_level
                || user?.user_metadata?.education_level
                || null;
            const { exercises: rankedExercises, recommendationMode: nextRecommendationMode } = selectRecommendedExercises({
                exercises: data || [],
                userLanguage,
                userSchoolLevel,
                submissions,
            });
            setRecommendationMode(nextRecommendationMode);
            setExercises(rankedExercises);
        } catch (err) {
            console.error('[ExerciseBrowser] Exercises error:', err);
            // Fallback without status filter
            try {
                let query = supabase
                    .from('exercises')
                    .select('*')
                    .order('created_at', { ascending: true });
                if (unitId) query = query.eq('unit_id', unitId);
                const { data } = await query.limit(100);
                setRecommendationMode('balanced');
                setExercises(data || []);
            } catch (e) {
                console.error('[ExerciseBrowser] Fallback error:', e);
            }
        } finally {
            setExercisesLoading(false);
        }
    }, [profile, searchQuery, submissions, user]);

    useEffect(() => {
        if (selectedUnit) {
            fetchExercises(selectedUnit.id);
        }
    }, [selectedUnit, fetchExercises]);

    const handleUnitClick = (unit) => {
        setSelectedUnit(unit);
        setSearchQuery('');
    };

    const handleBackToUnits = () => {
        setSelectedUnit(null);
        setExercises([]);
        setSearchQuery('');
    };

    const handleExerciseClick = (exercise) => {
        navigate(`/practice/exercise/${exercise.id}`);
    };

    const handleBrowseAll = () => {
        setSelectedUnit({ id: null, title: 'All Exercises', description: 'Browse all available exercises' });
        fetchExercises(null);
    };

    const filteredExercises = exercises;

    const getTypeIcon = (type) => {
        switch (type) {
            case 'multiple_choice': return '🔘';
            case 'numeric_input': return '🔢';
            case 'free_response': return '✍️';
            case 'true_false': return '✅';
            default: return '📝';
        }
    };

    const recommendationMessage = recommendationMode === 'easier'
        ? 'You are in support mode: easier exercises are shown first.'
        : recommendationMode === 'harder'
            ? 'You are on a streak: harder exercises are shown first.'
            : 'Exercises are balanced across difficulty.';

    return (
        <div className={`exercise-browser ${isDarkTheme ? 'dark' : 'light'}`}>
            <div className="browser-container">
                {!selectedUnit ? (
                    <>
                        {/* Units View */}
                        <div className="browser-header">
                            <div>
                                <h1>Practice</h1>
                                <p className="browser-subtitle">Choose a unit to start practicing</p>
                            </div>
                            <button className="browser-browse-all-btn" onClick={handleBrowseAll}>
                                Browse All Exercises
                            </button>
                        </div>

                        {loading ? (
                            <div className="browser-loading">Loading units...</div>
                        ) : units.length === 0 ? (
                            <div className="browser-empty">
                                <div className="browser-empty-icon">📚</div>
                                <h3>No units available yet</h3>
                                <p>Check back soon — new content is being added!</p>
                            </div>
                        ) : (
                            <div className="units-grid">
                                {units.map((unit, index) => (
                                    <div
                                        key={unit.id}
                                        className="unit-card card"
                                        onClick={() => handleUnitClick(unit)}
                                    >
                                        <div className="unit-card-number">Unit {index + 1}</div>
                                        <h3 className="unit-card-title">{unit.title}</h3>
                                        {unit.description && (
                                            <p className="unit-card-desc">{unit.description}</p>
                                        )}
                                        <div className="unit-card-arrow">→</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                ) : (
                    <>
                        {/* Exercises View */}
                        <div className="browser-header">
                            <div>
                                <button className="browser-back-btn" onClick={handleBackToUnits}>
                                    ← Back to Units
                                </button>
                                <h1>{selectedUnit.title}</h1>
                                {selectedUnit.description && (
                                    <p className="browser-subtitle">{selectedUnit.description}</p>
                                )}
                                <p className="browser-subtitle">{recommendationMessage} All difficulty levels are still available.</p>
                            </div>
                        </div>

                        <div className="browser-filters">
                            <div className="browser-search">
                                <span className="search-icon">🔍</span>
                                <input
                                    type="text"
                                    placeholder="Search exercises..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                            </div>
                        </div>

                        {exercisesLoading ? (
                            <div className="browser-loading">Loading exercises...</div>
                        ) : filteredExercises.length === 0 ? (
                            <div className="browser-empty">
                                <div className="browser-empty-icon">📋</div>
                                <h3>No exercises found</h3>
                                <p>Try adjusting your filters or check back later.</p>
                            </div>
                        ) : (
                            <div className="exercises-list">
                                {filteredExercises.map((exercise, index) => {
                                    const isCompleted = completedIds.has(exercise.id);
                                    return (
                                        <div
                                            key={exercise.id}
                                            className={`exercise-card card ${isCompleted ? 'completed' : ''}`}
                                            onClick={() => handleExerciseClick(exercise)}
                                        >
                                            <div className="exercise-card-left">
                                                <span className="exercise-card-index">{index + 1}</span>
                                                <div className="exercise-card-info">
                                                    <div className="exercise-card-title">
                                                        {exercise.title || 'Untitled Exercise'}
                                                    </div>
                                                    <div className="exercise-card-meta">
                                                        <span className="exercise-type-badge">
                                                            {getTypeIcon(exercise.exercise_type)}{' '}
                                                            {(exercise.exercise_type || 'exercise').replace(/_/g, ' ')}
                                                        </span>
                                                        {Array.isArray(exercise.tags) && exercise.tags.slice(0, 3).map((tag, i) => (
                                                            <span key={i} className="exercise-tag">{tag}</span>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="exercise-card-right">
                                                <span className="exercise-difficulty">{normalizeDifficulty(exercise)}</span>
                                                {isCompleted ? (
                                                    <span className="exercise-completed-badge">✓ Solved</span>
                                                ) : (
                                                    <span className="exercise-solve-btn">Solve →</span>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default ExerciseBrowser;
